/**
 * TSL helper functions for the GPU galaxy simulation.
 * Mirrors the helpers from dgreenheck/webgpu-galaxy.
 */
import {
  Fn,
  vec3,
  float,
  sin,
  cos,
  length,
  normalize,
  smoothstep,
  fract,
} from "three/tsl";

// Pseudo-random hash function from a single float seed -> [0, 1)
export const hash = Fn(([seed]: any) => {
  return fract(sin(seed.mul(12.9898).add(78.233)).mul(43758.5453));
});

/**
 * Differential rotation: inner particles rotate faster than outer particles
 * (galactic shear). Rotates the position around the Y axis based on its
 * radial distance.
 */
export const applyDifferentialRotation = Fn(
  ([position, rotationSpeed, deltaTime]: any) => {
    const r = length(vec3(position.x, float(0.0), position.z)).add(0.5);
    const angularVelocity = rotationSpeed.mul(2.5).div(r);
    const angle = angularVelocity.mul(deltaTime);
    const c = cos(angle);
    const s = sin(angle);
    const x = position.x.mul(c).sub(position.z.mul(s));
    const z = position.x.mul(s).add(position.z.mul(c));
    return vec3(x, position.y, z);
  },
);

/**
 * Mouse repulsion: pushes particles away from the mouse point
 * with smooth radial falloff. Returns a delta to add to position.
 */
export const applyMouseForce = Fn(
  ([position, mouse, mouseActive, force, radius, deltaTime]: any) => {
    const toMouse = position.sub(mouse);
    const dist = length(toMouse).add(0.0001);
    const falloff = smoothstep(radius, float(0.0), dist);
    const dir = normalize(toMouse);
    return dir.mul(falloff).mul(force).mul(mouseActive).mul(deltaTime);
  },
);

/**
 * Spring force: pulls particles back toward their target position with
 * configurable stiffness. Returns a delta to add to position.
 */
export const applySpringForce = Fn(
  ([position, target, strength, deltaTime]: any) => {
    const toTarget = target.sub(position);
    return toTarget.mul(strength).mul(deltaTime);
  },
);